<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto">
      <!-- Header with Actions -->
      <div class="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 mb-6">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div class="flex-1 min-w-0">
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 truncate">{{ resume.title }}</h1>
            <p v-if="resume.views !== undefined" class="text-sm text-gray-500 flex items-center gap-1">
              <icon name="aperture" class="w-4 h-4" />
              {{ resume.views }} {{ resume.views === 1 ? 'view' : 'views' }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <button
              @click="shareResume"
              :disabled="copied"
              :class="[
                'flex-1 sm:flex-none px-4 py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md text-sm font-medium',
                copied
                  ? 'bg-green-100 text-green-700 cursor-default'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
              ]"
            >
              <icon v-if="!copied" name="send" class="w-4 h-4" />
              <icon v-else name="tick" class="w-4 h-4" />
              {{ copied ? 'Link Copied!' : 'Share' }}
            </button>
            <button
              @click="downloadPDF"
              :disabled="downloading"
              class="flex-1 sm:flex-none px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg font-semibold"
            >
              <icon v-if="!downloading" name="download" class="w-5 h-5 text-white fill-white" />
              <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ downloading ? 'Generating...' : 'Download PDF' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Resume Content -->
      <div
        id="resume-content"
        class="resume-content bg-white shadow-2xl p-4 sm:p-8 md:p-12"
        :style="{
          fontFamily: resume.font_family || 'Inter',
          color: '#1f2937'
        }"
      >
        <component
          v-for="section in sortedSections"
          :key="section.id"
          :is="getSectionComponent(section.type)"
          :section="section"
          :theme-color="resume.theme_color"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import ProfileSection from './Sections/ProfileSection.vue'
import ExperienceSection from './Sections/ExperienceSection.vue'
import EducationSection from './Sections/EducationSection.vue'
import SkillsSection from './Sections/SkillsSection.vue'
import ProjectsSection from './Sections/ProjectsSection.vue'

export default {
  components: {
    Icon,
    ProfileSection,
    ExperienceSection,
    EducationSection,
    SkillsSection,
    ProjectsSection,
  },
  props: {
    resume: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'Resume'
    }
  },
  data() {
    return {
      downloading: false,
      copied: false,
    }
  },
  computed: {
    sortedSections() {
      return [...this.sections].sort((a, b) => a.order - b.order)
    }
  },
  methods: {
    getSectionComponent(type) {
      switch (type) {
        case 'profile':
          return 'ProfileSection'
        case 'experience':
          return 'ExperienceSection'
        case 'education':
          return 'EducationSection'
        case 'skills':
          return 'SkillsSection'
        case 'projects':
          return 'ProjectsSection'
        default:
          return 'div'
      }
    },
    async downloadPDF() {
      this.downloading = true
      try {
        // Dynamically import html2pdf to avoid SSR issues
        const html2pdf = (await import('html2pdf.js')).default
        const element = document.getElementById('resume-content')
        const opt = {
          margin: [10, 10, 10, 10],
          filename: `${this.resume.title || 'resume'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        }

        await html2pdf().set(opt).from(element).save()

        this.downloading = false
      } catch (error) {
        console.error('Error generating PDF:', error)
        if (window.$toast) {
          window.$toast.error('Failed to generate PDF. Please try again.')
        }
        this.downloading = false
      }
    },
    async shareResume() {
      const url = window.location.href
      try {
        if (navigator.share) {
          await navigator.share({
            title: this.resume.title,
            text: `Check out my resume: ${this.resume.title}`,
            url: url
          })
        } else {
          await navigator.clipboard.writeText(url)
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 3000)
        }
      } catch (error) {
        // Fallback to copy
        try {
          await navigator.clipboard.writeText(url)
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 3000)
        } catch (err) {
          console.error('Failed to share:', err)
        }
      }
    }
  },
  watch: {
    'resume.title': {
      immediate: true,
      handler(newTitle) {
        document.title = newTitle ? `${newTitle} - Resume` : 'Resume'
      }
    }
  }
}
</script>

<style scoped>
.resume-content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  line-height: 1.6;
}

@media print {
  .resume-content {
    box-shadow: none;
    margin: 0;
    padding: 0;
  }
}
</style>
